import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { apiFetch } from '../utils/api';
import {
  CheckCircle2,
  Clock,
  DollarSign,
  TrendingUp,
  FileText,
  Plus,
  X,
  Save,
  AlertCircle,
  Star,
  Calendar,
  Loader2,
  Filter,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';

interface ClosedReport {
  id: string;
  claimant_name: string;
  business_type: string;
  event_type: string;
  claim_amount: number;
  status: string;
  created_at: string;
  has_outcome: boolean;
  outcome?: ReportOutcome;
}

interface ReportOutcome {
  id: string;
  report_id: string;
  final_settlement_amount: number;
  settlement_date: string;
  settlement_type: string;
  time_to_resolution_days?: number;
  satisfaction_rating?: number;
  notes?: string;
  settlement_ratio?: number;
  submitted_at: string;
}

interface OutcomeStats {
  total_outcomes: number;
  average_settlement_ratio: number;
  average_time_to_resolution: number;
  average_satisfaction: number;
  settlement_type_breakdown: Record<string, number>;
  industry_breakdown: Record<string, number>;
  event_type_breakdown: Record<string, number>;
}

const settlementTypes = [
  { value: 'negotiated', label: 'Negotiated Settlement' },
  { value: 'mediated', label: 'Mediated Settlement' },
  { value: 'arbitrated', label: 'Arbitrated' },
  { value: 'litigated', label: 'Litigated (Court Decision)' },
  { value: 'denied', label: 'Claim Denied' },
  { value: 'withdrawn', label: 'Claim Withdrawn' },
];

export default function ClosedCasesPage() {
  const { getToken } = useAuth();
  
  const [reports, setReports] = useState<ClosedReport[]>([]);
  const [stats, setStats] = useState<OutcomeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ClosedReport | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    final_settlement_amount: '',
    settlement_date: '',
    settlement_type: 'negotiated',
    time_to_resolution_days: '',
    satisfaction_rating: '',
    notes: ''
  });
  
  // Filter state
  const [filterType, setFilterType] = useState<'all' | 'with_outcome' | 'without_outcome'>('all');
  const [showStats, setShowStats] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch closed reports
      const reportsData = await apiFetch<ClosedReport[]>(
        '/api/v1/outcomes/closed-reports?limit=100',
        { headers }
      );
      setReports(reportsData);
      
      // Fetch stats
      try {
        const statsData = await apiFetch<OutcomeStats>(
          '/api/v1/outcomes/stats',
          { headers, showErrorToast: false }
        );
        setStats(statsData);
      } catch {
        // Stats may fail if no outcomes yet
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openOutcomeModal = (report: ClosedReport) => {
    setSelectedReport(report);
    if (report.outcome) {
      // Pre-fill form with existing outcome
      setFormData({
        final_settlement_amount: report.outcome.final_settlement_amount.toString(),
        settlement_date: report.outcome.settlement_date,
        settlement_type: report.outcome.settlement_type,
        time_to_resolution_days: report.outcome.time_to_resolution_days?.toString() || '',
        satisfaction_rating: report.outcome.satisfaction_rating?.toString() || '',
        notes: report.outcome.notes || ''
      });
    } else {
      // Reset form
      setFormData({
        final_settlement_amount: '',
        settlement_date: new Date().toISOString().split('T')[0],
        settlement_type: 'negotiated',
        time_to_resolution_days: '',
        satisfaction_rating: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const submitOutcome = async () => {
    if (!selectedReport) return;
    
    try {
      setSubmitting(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        report_id: selectedReport.id,
        final_settlement_amount: parseFloat(formData.final_settlement_amount),
        settlement_date: formData.settlement_date,
        settlement_type: formData.settlement_type,
        time_to_resolution_days: formData.time_to_resolution_days ? parseInt(formData.time_to_resolution_days) : null,
        satisfaction_rating: formData.satisfaction_rating ? parseInt(formData.satisfaction_rating) : null,
        notes: formData.notes || null,
        claim_amount_original: selectedReport.claim_amount
      };
      
      const endpoint = selectedReport.has_outcome 
        ? `/api/v1/outcomes/report/${selectedReport.id}`
        : '/api/v1/outcomes/submit';
      
      const method = selectedReport.has_outcome ? 'PUT' : 'POST';
      
      await apiFetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(payload)
      });
      
      setShowModal(false);
      fetchData(); // Refresh data
      
    } catch (err: any) {
      setError(err.message || 'Failed to submit outcome');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredReports = reports.filter(r => {
    if (filterType === 'with_outcome') return r.has_outcome;
    if (filterType === 'without_outcome') return !r.has_outcome;
    return true;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (ratio: number) => {
    return `${(ratio * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Closed Cases</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track settlement outcomes and build internal benchmarks for better analysis
        </p>
      </div>

      {/* Stats Cards */}
      {stats && stats.total_outcomes > 0 && (
        <div className="mb-8">
          <button 
            onClick={() => setShowStats(!showStats)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Benchmark Statistics</span>
            {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {showStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_outcomes}</p>
                    <p className="text-xs text-gray-500">Total Outcomes</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{formatPercent(stats.average_settlement_ratio)}</p>
                    <p className="text-xs text-gray-500">Avg Settlement Ratio</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{Math.round(stats.average_time_to_resolution)} days</p>
                    <p className="text-xs text-gray-500">Avg Resolution Time</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Star className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.average_satisfaction.toFixed(1)}/5</p>
                    <p className="text-xs text-gray-500">Avg Satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="text-sm border-gray-200 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Completed Cases ({reports.length})</option>
            <option value="without_outcome">Awaiting Outcome ({reports.filter(r => !r.has_outcome).length})</option>
            <option value="with_outcome">Outcome Submitted ({reports.filter(r => r.has_outcome).length})</option>
          </select>
        </div>
        
        <p className="text-sm text-gray-500">
          {filteredReports.length} cases shown
        </p>
      </div>

      {/* Reports List */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claimant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Settlement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ratio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No completed cases found</p>
                    <p className="text-sm">Complete some claims to start tracking outcomes</p>
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{report.claimant_name}</p>
                        <p className="text-xs text-gray-500">{report.business_type} • {report.event_type}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(report.claim_amount)}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.has_outcome && report.outcome ? (
                        <div>
                          <p className="text-sm font-medium text-green-600">
                            {formatCurrency(report.outcome.final_settlement_amount)}
                          </p>
                          <p className="text-xs text-gray-500">{report.outcome.settlement_type}</p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.outcome?.settlement_ratio ? (
                        <span className={`text-sm font-medium ${
                          report.outcome.settlement_ratio >= 0.9 ? 'text-green-600' :
                          report.outcome.settlement_ratio >= 0.7 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {formatPercent(report.outcome.settlement_ratio)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.has_outcome ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Outcome Recorded
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Clock className="w-3.5 h-3.5" />
                          Awaiting Outcome
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openOutcomeModal(report)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                          report.has_outcome 
                            ? 'text-gray-600 hover:bg-gray-100 border border-gray-200'
                            : 'text-white bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {report.has_outcome ? (
                          <>Edit</>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            Add Outcome
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Outcome Modal */}
      {showModal && selectedReport && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowModal(false)}
            />
            
            <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedReport.has_outcome ? 'Edit Outcome' : 'Record Outcome'}
                    </h3>
                    <p className="text-sm text-gray-500">{selectedReport.claimant_name}</p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 space-y-4">
                {/* Claim Amount Reference */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Original Claim Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatCurrency(selectedReport.claim_amount)}
                  </p>
                </div>
                
                {/* Final Settlement Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Final Settlement Amount *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.final_settlement_amount}
                      onChange={(e) => setFormData({ ...formData, final_settlement_amount: e.target.value })}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                
                {/* Settlement Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Settlement Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={formData.settlement_date}
                      onChange={(e) => setFormData({ ...formData, settlement_date: e.target.value })}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                
                {/* Settlement Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Settlement Type *
                  </label>
                  <select
                    value={formData.settlement_type}
                    onChange={(e) => setFormData({ ...formData, settlement_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    {settlementTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Time to Resolution */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time to Resolution (days)
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.time_to_resolution_days}
                      onChange={(e) => setFormData({ ...formData, time_to_resolution_days: e.target.value })}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                
                {/* Satisfaction Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Satisfaction Rating
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ 
                          ...formData, 
                          satisfaction_rating: formData.satisfaction_rating === rating.toString() ? '' : rating.toString()
                        })}
                        className={`flex-1 py-2 rounded-lg border transition ${
                          formData.satisfaction_rating === rating.toString()
                            ? 'border-amber-500 bg-amber-50 text-amber-600'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Star className={`w-5 h-5 mx-auto ${
                          formData.satisfaction_rating === rating.toString() ? 'fill-amber-500' : ''
                        }`} />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any additional notes about the outcome..."
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitOutcome}
                  disabled={submitting || !formData.final_settlement_amount || !formData.settlement_date}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Outcome
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
